import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { authAxios, apiErrorMessage } from '@/lib/api';
import { logError } from '@/lib/logger';
import { ImageUploader } from './ImageUploaders';

const PHOTO_SLOTS = [
  { key: 'logo', label: 'Website Logo', desc: 'Company logo displayed in navbar and footer. Recommended: PNG with transparent background, 200x60px or similar' },
  { key: 'hero_bg', label: 'Homepage Hero Background', desc: 'Main background image on the landing page hero section' },
  { key: 'cta_bg', label: 'Homepage CTA Background', desc: 'Background image for the "Ready to Source" call-to-action section' },
  { key: 'about_story', label: 'About Page Story Image', desc: 'Image displayed next to "Our Journey" story text' },
];

export default function SitePhotosManager() {
  const [sitePhotos, setSitePhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    authAxios.get('/api/site-photos', { signal: controller.signal })
      .then(r => { setSitePhotos(r.data); setLoading(false); })
      .catch(error => {
        if (controller.signal.aborted) return;
        logError('Failed to load site photos:', error);
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const handlePhotoUpdate = (key, url) => {
    setSitePhotos(prev => ({
      ...prev,
      [key]: { ...prev[key], url }
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(sitePhotos).map(([key, val]) => ({
        key,
        url: val.url || '',
        label: val.label || PHOTO_SLOTS.find(s => s.key === key)?.label || ''
      }));
      await authAxios.put('/api/site-photos', updates);
      alert('Site photos saved successfully!');
    } catch (e) {
      alert('Save failed: ' + apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-earth/40 font-sans">Loading site photos...</div>;
  }

  return (
    <div className="space-y-6" data-testid="site-photos-manager">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-earth/50 font-sans">Change any photo displayed on the website. Upload a new image or paste a URL.</p>
        </div>
        <button
          onClick={handleSaveAll}
          disabled={saving}
          className="flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-xs px-5 py-2.5 rounded-md hover:bg-saffron-dark transition-colors disabled:opacity-50"
          data-testid="save-site-photos"
        >
          <Save size={14} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PHOTO_SLOTS.map(slot => (
          <div key={slot.key} className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-5" data-testid={`site-photo-${slot.key}`}>
            <h4 className="font-display text-sm font-bold text-earth-dark mb-1">{slot.label}</h4>
            <p className="text-[10px] text-earth/40 font-sans mb-3">{slot.desc}</p>

            <ImageUploader
              currentImage={sitePhotos[slot.key]?.url || ''}
              onUpload={(url) => handlePhotoUpdate(slot.key, url)}
              label="Upload New Image"
            />

            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-wider text-earth/40 font-sans font-semibold mb-1 block">Or paste URL</label>
              <input
                value={sitePhotos[slot.key]?.url || ''}
                onChange={(e) => handlePhotoUpdate(slot.key, e.target.value)}
                placeholder="https://..."
                className="w-full border border-earth/10 rounded px-2.5 py-1.5 text-xs font-sans focus:outline-none focus:ring-1 focus:ring-saffron/30 bg-cream/30"
                data-testid={`site-photo-url-${slot.key}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
